import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { 
  Search, 
  Plus, 
  Users, 
  UserCheck,
  Check,
  X,
  Mail,
  Download,
  Filter,
  CheckCircle
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import ClassWizardLayout from './ClassWizardLayout';
import { useClassWizard } from './ClassWizardContext';
import { Subscription } from '@/types';
import ImportHubspotContactDialog from '@/components/customers/ImportHubspotContactDialog';

interface CustomerContact {
  id: string;
  customer_id: string;
  first_name: string;
  last_name: string | null;
  email: string;
  phone: string | null;
  is_active?: boolean;
  job_title?: string | null;
}

// Definir a interface extendida para Subscription com subscription_customers
interface SubscriptionWithRelations extends Subscription {
  subscription_customers: {
    id: string;
    customer_role: 'owner' | 'participant' | 'billing_contact';
    customer: {
      id: string;
      name: string;
    };
  }[];
  parent_subscription?: {
    id: string;
    display_id: string;
  };
}

const Step6Sponsors = () => {
  const navigate = useNavigate();
  const { state, setState } = useClassWizard();
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<CustomerContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>(
    state.selectedSponsors || []
  );
  const [isImportContactDialogOpen, setIsImportContactDialogOpen] = useState(false);
  const [contactsRefreshTrigger, setContactsRefreshTrigger] = useState(0);

  useEffect(() => {
    // Validações dos passos anteriores
    if (!state.selectedCustomer) {
      toast.error('Selecione um cliente primeiro');
      navigate('/class-wizard/step1');
      return;
    }
    
    if (!state.selectedSubscription) {
      toast.error('Selecione uma assinatura primeiro');
      navigate('/class-wizard/step2');
      return;
    }
    
    // Buscar contatos do cliente principal (owner da assinatura)
    const ownerCustomer = state.selectedSubscription.subscription_customers.find(sc => sc.customer_role === 'owner');
    if (!ownerCustomer) {
      toast.error('Cliente proprietário da assinatura não encontrado');
      navigate('/class-wizard/step2');
      return;
    }
    
    console.log('Carregando contatos para o cliente:', ownerCustomer.customer.id);
    fetchContacts(ownerCustomer.customer.id);
  }, [state, navigate]);
  
  useEffect(() => {
    // Filtrar contatos com base no termo de busca
    filterContacts();
  }, [contacts, searchTerm]);
  
  const filterContacts = () => {
    if (!searchTerm.trim()) {
      setFilteredContacts(contacts);
      return;
    }
    
    const term = searchTerm.toLowerCase();
    const filtered = contacts.filter(contact => 
      contact.first_name.toLowerCase().includes(term) || 
      (contact.last_name && contact.last_name.toLowerCase().includes(term)) ||
      contact.email.toLowerCase().includes(term) ||
      (contact.phone && contact.phone.toLowerCase().includes(term))
    );
    
    setFilteredContacts(filtered);
  };

  const fetchContacts = async (childId: string) => {
    setLoading(true);
    try {
      console.log(`Iniciando busca de contatos para o cliente ID: ${childId}`);
      
      // Buscar contatos relacionados ao cliente da assinatura
      const { data, error } = await supabase
        .from('customer_contacts')
        .select('*')
        .eq('customer_id', childId)
        .eq('is_active', true);

      if (error) {
        console.error('Erro SQL ao buscar contatos:', error);
        throw error;
      }
      
      if (data && data.length > 0) {
        console.log(`Encontrados ${data.length} contatos para o cliente ${childId}:`, data);
        setContacts(data);
        setFilteredContacts(data);
      } else {
        console.log(`Nenhum contato encontrado para o cliente ${childId}. Verifique se o ID está correto e se existem contatos cadastrados.`);
        toast.warning('Nenhum contato encontrado para este cliente');
        setContacts([]);
        setFilteredContacts([]);
      }
    } catch (error: any) {
      console.error('Erro ao buscar contatos:', error);
      toast.error('Não foi possível carregar os contatos do cliente', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (contactId: string, checked: boolean) => {
    if (checked) {
      setSelectedContactIds(prev => [...prev, contactId]);
    } else {
      setSelectedContactIds(prev => prev.filter(id => id !== contactId));
    }
  };

  const handleSelectAll = () => {
    if (selectedContactIds.length === filteredContacts.length) {
      // Se todos estão selecionados, desmarca todos
      setSelectedContactIds([]);
    } else {
      // Seleciona todos os contatos filtrados
      setSelectedContactIds(filteredContacts.map(contact => contact.id));
    }
  };

  const handleNext = () => {
    if (selectedContactIds.length === 0) {
      toast.error('Selecione pelo menos um contato solicitante');
      return;
    }
    
    // Salvar os contatos selecionados no contexto
    setState(prev => ({ ...prev, selectedSponsors: selectedContactIds }));
    
    // Navegar para o próximo passo
    navigate('/class-wizard/step4');
  };

  // New function to handle contact imported
  const handleContactImported = () => {
    setContactsRefreshTrigger(prev => prev + 1);
    // Refresh contacts after import
    const ownerCustomer = state.selectedSubscription?.subscription_customers.find(sc => sc.customer_role === 'owner');
    if (ownerCustomer) {
      fetchContacts(ownerCustomer.customer.id);
    }
  };

  return (
    <ClassWizardLayout
      currentStep={3}
      title="Selecionar Solicitantes"
      description="Selecione os contatos do cliente que serão os solicitantes da turma"
      onNext={handleNext}
      nextDisabled={selectedContactIds.length === 0}
    >
      <div className="space-y-6">
        {/* Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                <Users className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Contatos Disponíveis</p>
                {loading ? (
                  <Skeleton className="h-7 w-16 mt-1" />
                ) : (
                  <p className="text-2xl font-extrabold">{contacts.length}</p>
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-100 text-green-600">
                <UserCheck className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Solicitantes Selecionados</p>
                <p className="text-2xl font-extrabold">{selectedContactIds.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-full bg-amber-100 text-amber-600">
                <CheckCircle className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Cliente</p>
                <p className="text-lg font-extrabold truncate">
                  {state.selectedSubscription?.subscription_customers?.find(sc => sc.customer_role === 'owner')?.customer?.name || 'Cliente'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Barra de busca e botão de importar */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative flex w-full max-w-sm items-center">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Buscar contatos..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <Button 
            variant="outline" 
            onClick={() => setIsImportContactDialogOpen(true)}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            Importar Contato
          </Button>
        </div>
        
        {/* Tabela de contatos */}
        <Card>
          <CardContent className="p-4">
            {loading ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : filteredContacts.length === 0 ? (
              <div className="text-center py-8">
                <Users className="mx-auto h-12 w-12 text-muted-foreground/50" />
                <h3 className="mt-2 text-lg font-extrabold">Nenhum contato encontrado</h3>
                <p className="text-sm text-muted-foreground">
                  {contacts.length === 0 ? 
                    'Não há contatos disponíveis para este cliente.' : 
                    'Tente ajustar sua pesquisa para encontrar contatos.'}
                </p>
                {contacts.length === 0 && (
                  <Button 
                    variant="outline" 
                    onClick={() => setIsImportContactDialogOpen(true)}
                    className="mt-4 gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Importar Contato do HubSpot
                  </Button>
                )}
              </div>
            ) : (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center">
                    <Checkbox
                      id="select-all"
                      checked={filteredContacts.length > 0 && 
                        selectedContactIds.length === filteredContacts.length}
                      onCheckedChange={handleSelectAll}
                    />
                    <label 
                      htmlFor="select-all" 
                      className="text-sm font-extrabold ml-2 cursor-pointer"
                    >
                      Selecionar todos
                    </label>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {selectedContactIds.length} de {filteredContacts.length} selecionados
                  </div>
                </div>
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]"></TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Cargo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredContacts.map((contact) => (
                      <TableRow key={contact.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedContactIds.includes(contact.id)}
                            onCheckedChange={(checked) => 
                              handleCheckboxChange(contact.id, !!checked)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <div className="font-extrabold">
                            {contact.first_name} {contact.last_name || ''}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center">
                            <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
                            {contact.email}
                          </div>
                        </TableCell>

                        <TableCell>
                          {contact.job_title || (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Import Contact Dialog */}
      <ImportHubspotContactDialog
        customerId={state.selectedSubscription?.subscription_customers?.find(sc => sc.customer_role === 'owner')?.customer?.id || ''}
        isOpen={isImportContactDialogOpen}
        onClose={() => setIsImportContactDialogOpen(false)}
        onContactImported={handleContactImported}
      />
    </ClassWizardLayout>
  );
};

export default Step6Sponsors; 